import { PageTitle } from '../../../../_metronic/layout/core'
import { Button } from '../../../components/Button/Button'
import { Loading } from '../../../components/Loading/Loading'
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import { useFeaturesPluginsSpace } from './factory/useFeaturesPluginspace'
import { createActionsPluginSpace } from './factory/generateActions'
import { createActionsFeatureSpace } from './factory/generateActionsFeature'
import { generateColumnsPluginSpace } from './factory/generateColumns'
import { generateColumnsFeatureSpace } from './factory/generateColumnsFeature'
import { usePluginsSpace } from './factory/usePluginspaces'
import { ModularDataTable } from '../../../components/ModularDataTable'
import { BreadCrumbs } from '../../../components/BreadCrumbs/breadCrumbrs'
import { i18n } from '../../../translate/i18n'
import { ModalFeaturesPluginspace } from './components/modal'
import type { Feature, PluginSpace } from './types'

const FeaturesPluginspaceContent: FC = () => {
    const navigate = useNavigate()
    const { pluginSpaces, setLoading, getPluginSpaces, handlePaginationChange, handleDataChange, handleSortingChange, loading, pagination, sorting, tableLoading } = usePluginsSpace()
    const { getAllFeatures, features, getAllFeaturesOfPluginspace, createFeatureForPluginspace, removeFeatureForPluginspace, deleteFeature } = useFeaturesPluginsSpace()
    const [selectedPluginspace, setSelectedPluginspace] = useState<PluginSpace | null>(null)
    const [isFeaturesModalOpen, setIsFeaturesModalOpen] = useState(false)
    const [tab, setTab] = useState<'features' | 'pluginspaces'>('pluginspaces')
    const tabs = useMemo(() => [
        { id: 'pluginspaces', label: i18n.t('features_pluginspace.tabs.pluginspaces') },
        { id: 'features', label: i18n.t('features_pluginspace.tabs.features') },
    ], [])

    const columns = useMemo(
        () => generateColumnsPluginSpace(),
        []
    )

    const featureColumns = useMemo(
        () => generateColumnsFeatureSpace(),
        []
    )

    const handleManageFeatures = useCallback((pluginspace: PluginSpace) => {
        setSelectedPluginspace(pluginspace)
        setIsFeaturesModalOpen(true)
    }, [])

    const actions = useMemo(
        () => createActionsPluginSpace({ onManageFeatures: handleManageFeatures }),
        [handleManageFeatures]
    )

    const handleEditFeature = useCallback((feature: Feature) => {
        navigate(`/features_pluginspaces/update/${feature.id}`)
    }, [navigate])

    const handleDeleteFeature = useCallback(async (feature: Feature) => {
        const result = await Swal.fire({
            title: 'Excluir feature?',
            text: `Confirma a exclusao da feature ${feature.name}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#7e8299',
            cancelButtonText: 'Cancelar',
            confirmButtonText: 'Excluir',
        })

        if (!result.isConfirmed) {
            return
        }

        const success = await deleteFeature(feature.id)

        if (success) {
            await Swal.fire({
                icon: 'success',
                title: 'Feature excluida',
                text: 'A feature foi removida com sucesso.',
            })
        } else {
            await Swal.fire({
                icon: 'error',
                title: 'Erro ao excluir feature',
                text: 'Nao foi possivel excluir a feature.',
            })
        }
    }, [deleteFeature])

    const featureActions = useMemo(
        () => createActionsFeatureSpace({ onEdit: handleEditFeature, onDelete: handleDeleteFeature }),
        [handleDeleteFeature, handleEditFeature]
    )

    const loadPage = useCallback(async () => {
        setLoading(true)
        await getPluginSpaces()
        await getAllFeatures()
        setLoading(false)
    }, [])

    useEffect(() => {
        loadPage()
    }, [loadPage])

    if (loading) {
        return <Loading />
    }

    const isPluginspacesTab = tab === 'pluginspaces'
    const isFeaturesTab = tab === 'features'


    return (
        <div className='row g-5 gx-xxl-12'>
            <div className='card card-xxl-stretch mb-5 mb-xl-12'>
                <div className='card-header py-5'>
                    <h3 className='card-title align-items-start flex-column'>
                        <span className='card-label fw-bolder fs-3 mb-1'>
                            {i18n.t(`features_pluginspace.title`)}
                        </span>
                        <BreadCrumbs />
                        <span className='text-muted mt-1 fw-bold fs-7'>
                            {i18n.t(
                                `features_pluginspace.subTitle`
                            )}
                        </span>
                    </h3>


                </div>

                <div className='d-flex justify-content-between align-items-center my-4'>
                    <div className='nav nav-pills nav-pills-custom gap-2'>
                        {tabs.map((tabA) => (
                            <div
                                className={`nav-link px-5 py-3 fw-bold rounded-3 cursor-pointer ${tabA.id === tab ? 'active bg-primary text-white' : 'bg-light text-gray-700'}`}
                                key={tabA.id}
                                onClick={() => setTab(tabA.id as 'features' | 'pluginspaces')}
                            >
                                {tabA.label}
                            </div>
                        ))}
                    </div>

                    {isFeaturesTab && (
                        <Button
                            text='Criar Feature'
                            color='primary'
                            icon='fas fa-plus'
                            iconColor='#FFF'
                            size='small'
                            horizontalPadding={6}
                            btnAttr={{ type: 'button' }}
                            click={() => navigate('/features_pluginspaces/create')}
                            btnClass='fw-bolder'
                        />
                    )}
                </div>

                <div className='card-body'>
                    {isPluginspacesTab ? (
                        <ModularDataTable
                            data={pluginSpaces}
                            columns={columns}
                            actions={actions}
                            pagination={pagination}
                            onPaginationChange={handlePaginationChange}
                            sorting={sorting}
                            onSortingChange={handleSortingChange}
                            onDataChange={handleDataChange}
                            searchable={true}
                            searchPlaceholder='Pesquisar pluginSpaces...'
                            loading={tableLoading}
                            selectable={false}
                            striped={false}
                            hover={false}
                            size='md'
                            className='table-row-bordered table-row-gray-200'
                            alterColumns
                            persistPageKey="features_pluginspaces"
                            persistContextKey="default"
                        />
                    ) : (
                        <ModularDataTable
                            data={features}
                            columns={featureColumns}
                            actions={featureActions}
                            searchable={true}
                            searchPlaceholder='Pesquisar features...'
                            loading={tableLoading}
                            selectable={false}
                            striped={false}
                            hover={false}
                            size='md'
                            className='table-row-bordered table-row-gray-200'
                            alterColumns
                            persistPageKey='features_pluginspaces_features'
                            persistContextKey='default'
                        />
                    )}
                </div>
            </div>

            <ModalFeaturesPluginspace
                isOpen={isFeaturesModalOpen}
                pluginspace={selectedPluginspace}
                allFeatures={features}
                getAllFeatures={getAllFeatures}
                getAllFeaturesOfPluginspace={getAllFeaturesOfPluginspace}
                createFeatureForPluginspace={createFeatureForPluginspace}
                removeFeatureForPluginspace={removeFeatureForPluginspace}
                onClose={() => setIsFeaturesModalOpen(false)}
            />
        </div>
    )
}


const FeaturesPluginspace: FC = () => {
    return (
        <>
            <PageTitle
                breadcrumbs={[
                    { title: 'Features Pluginspace', path: 'features_pluginspaces', isActive: true },
                ]}
            >
                Features Pluginspace
            </PageTitle>
            <FeaturesPluginspaceContent />
        </>
    )
}

export { FeaturesPluginspace }